import axios from 'axios'

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

/** Registered by auth store at init time to break circular dependency */
let _getAuthContext:
  | (() => { userId?: number; tenantId?: number; orgId?: number })
  | null = null

export function registerAuthContextProvider(
  fn: () => { userId?: number; tenantId?: number; orgId?: number },
) {
  _getAuthContext = fn
}

// Inject Bearer token + request context headers on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Cabeçalhos de ESCOPO (seleção), nunca de identidade.
  //
  // A-07 (TASK-2822) removeu x-consoleai-user-id — correto: era spoofável e
  // permitia escalada (baixo privilégio → id de admin no gate). A identidade
  // vem da sessão (C-02) e não voltará por header.
  //
  // O x-consoleai-tenant-id, porém, é SELETOR de escopo, igual ao
  // x-consoleai-org-id: as rotas com tenant no path comparam
  // `tenant_id != ctx.tenant_id` e NEGAM na divergência, então o header
  // restringe — não eleva. Removê-lo junto quebrou 17 rotas com
  // 400 missing_request_context (regressão 2026-07-25). O backend valida o
  // valor contra o alcance real do usuário e ignora o que estiver fora.
  if (_getAuthContext) {
    const ctx = _getAuthContext()
    if (ctx.tenantId != null) {
      config.headers['x-consoleai-tenant-id'] = String(ctx.tenantId)
    }
    if (ctx.orgId != null) {
      config.headers['x-consoleai-org-id'] = String(ctx.orgId)
    }
  }

  return config
})

// Redirect to login on 401, EXCETO em endpoints de auth publicos que usam 401
// como parte do fluxo normal (totp_required, totp_invalid, code_invalid_or_expired etc).
const _AUTH_FLOW_PATHS = ['/auth/login', '/auth/forgot-totp/verify']

// Guard p/ não disparar múltiplos redirects (vários 401 concorrentes).
let _authRedirecting = false

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err.response?.status
    const cfg = err.config ?? {}
    const url = cfg.url ?? ''
    const isAuthFlow = _AUTH_FLOW_PATHS.some((p) => url.endsWith(p))

    // Férias (spec 2026-07-24 §3.3): o backend recusa TODA request autenticada
    // com 403 {detail: {code: 'on_leave', until}} enquanto o colaborador está
    // afastado. Sem isso a SPA mostraria erro genérico em cada tela. O teste
    // de `code` é obrigatório: 403 de permissão é comum e NÃO pode deslogar.
    if (status === 403) {
      const detail = err.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.code === 'on_leave') {
        if (!_authRedirecting) {
          _authRedirecting = true
          if (detail.until) {
            sessionStorage.setItem('on_leave_until', String(detail.until))
          }
          localStorage.removeItem('session_token')
          window.location.href = '/ferias'
        }
        return Promise.reject(err)
      }
    }

    // Só reagimos a 401 confirmado. 5xx, timeout e erro de rede (sem response,
    // ex: blip durante deploy) NÃO deslogam — apenas rejeitam p/ o caller tratar.
    if (status === 401 && !isAuthFlow) {
      const method = String(cfg.method ?? 'get').toLowerCase()
      // Retry ÚNICO p/ requests idempotentes (GET/HEAD): absorve 401 transitório
      // (race/restart) sem risco de double-submit em POST/PUT/PATCH/DELETE.
      if ((method === 'get' || method === 'head') && !cfg._retried401) {
        cfg._retried401 = true
        try {
          return await api(cfg)
        } catch {
          // continua para o logout abaixo se o retry também falhar
        }
      }
      if (!_authRedirecting) {
        _authRedirecting = true
        localStorage.removeItem('session_token')
        // M-06 (TASK-2830): limpa o cache do react-query (memória + snapshot
        // persistido) também no logout involuntário por 401 — não reter PII.
        void import('@/lib/queryCache')
          .then((m) => m.clearPersistedQueryCache())
          .catch(() => {})
        // Limpa o store local de busca (plaintext no IndexedDB) também no
        // logout involuntário por 401. Best-effort — nunca bloqueia o redirect.
        void import('@/lib/messageStore')
          .then((m) => m.messageStore.clear())
          .catch(() => {})
        // Porta de entrada limpa: guarda a página atual p/ voltar após o
        // re-login e manda p/ a tela estática /session-expired (sem TTL), em
        // vez de auto-disparar o fluxo OIDC aqui. Não guardar as próprias telas
        // de auth (evita loop / return_to inútil).
        const here = window.location.pathname + window.location.search
        const ret =
          here.startsWith('/login') || here.startsWith('/session-expired')
            ? '/'
            : here
        sessionStorage.setItem('post_login_return_to', ret)
        window.location.href = '/session-expired'
      }
    }
    return Promise.reject(err)
  },
)
